import style from "./battery.scss";

import { Fragment } from "preact";
import { mergeClasses } from "../../components/helpers";
import Link, { Button } from "../../components/link";
import Image from "../../components/image";

const formatWh = (value, showUnits = true) => {
  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1, // Keeps things like 1.2kW clean
  }).format(value);

  return `${formattedNumber}${showUnits ? "Wh" : ""}`;
};

const formatkWh = (value, showUnits = true) => {
  const kwValue = Number(value) / 1000;

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1, // Keeps things like 1.2kW clean
  }).format(kwValue);

  return `${formattedNumber}${showUnits ? "kWh" : ""}`;
};

const RenderNote = ({ note }) => {
  if (!note) return null;

  // Plain string note
  if (typeof note === "string") {
    return <span>{note}</span>;
  }

  // Structured object note
  if (typeof note === "object" && note.text) {
    const { text, links = {} } = note;

    // Splits text by {curly_brackets} while preserving the brackets in matches
    const parts = text.split(/(\{.*?\})/g);

    return (
      <span>
        {parts.map((part, index) => {
          // Check if this part is a token
          if (part.startsWith("{") && part.endsWith("}")) {
            const tokenKey = part.slice(1, -1); // Extracts "Smart Generator Adapter"
            const linkConfig = links[tokenKey];

            if (linkConfig) {
              return (
                <Link key={index} {...linkConfig}>
                  {tokenKey}
                </Link>
              );
            }
            // Fallback if token key isn't in links map
            return tokenKey;
          }

          // Plain text chunk
          return <Fragment key={index}>{part}</Fragment>;
        })}
      </span>
    );
  }

  return null;
};

const PowerSystem = ({ product, battery = null, notes = null, HeadingTag }) => {
  const hasNotes = notes && notes.length > 0;

  return (
    <div
      class={mergeClasses([
        style.powerSystem,
        hasNotes ? style.hasNotes : null,
      ])}
    >
      <div class={style.img}>
        <Image
          src={product.img}
          alt={`${product.brand} ${product.name}`}
          showAlt
          loading="lazy"
        />
      </div>

      <div class={style.name}>
        <HeadingTag class={style.productName}>{product.name}</HeadingTag>
      </div>

      <dl class={style.data}>
        <div class={style.dataItem}>
          <dt class={style.label}>Capacity</dt>
          <dd class={style.value}>
            {product.modular ? (
              <Fragment>
                <span>{`${formatkWh(product.capacityMin, false)}-${formatkWh(product.capacityMax)}`}</span>
              </Fragment>
            ) : (
              <Fragment>
                <span class={style.short}>{formatkWh(product.capacity)}</span>
                <span class={style.full}>{formatWh(product.capacity)}</span>
              </Fragment>
            )}
          </dd>
        </div>
        {product.expansion?.compatibleBatteries ? (
          <Fragment>
            <div class={style.dataItem}>
              <dt class={style.label}>Port</dt>
              <dd class={style.value}>{product?.expansion?.portType}</dd>
            </div>
            <div class={style.dataItem}>
              <dt class={style.label}>Max Battery</dt>
              <dd class={style.value}>
                {product?.expansion?.maxExtraBatteries}x
              </dd>
            </div>

            {battery && product?.expansion?.portType != battery?.portType ? (
              <div class={style.dataItem}>
                <dt class={style.label}>Adapter</dt>
                <dd class={style.value}>
                  <span class={style.short}>Yes</span>
                  <span class={style.full}>Required</span>
                </dd>
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </dl>

      <Buttons product={product} />

      {hasNotes ? (
        <div class={style.notes}>
          <ul>
            {notes?.map((note, index) => (
              <li key={index}>
                <RenderNote note={note} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const Battery = ({ product, system = null, notes = null, HeadingTag }) => {
  const hasNotes = notes && notes.length > 0;

  return (
    <div
      class={mergeClasses([style.battery, hasNotes ? style.hasNotes : null])}
    >
      <div class={style.img}>
        <Image
          src={product.img}
          alt={`${product.brand} ${product.name}`}
          showAlt
          loading="lazy"
        />
      </div>

      <div class={style.name}>
        <HeadingTag class={style.productName}>
          {product.name
            .split(/(Smart Extra Battery|Extra Battery)/)
            .map((part, i) =>
              part === "Smart Extra Battery" || part === "Extra Battery" ? (
                <Fragment key={i}>
                  <br class={style.breakName} />
                  {part}
                </Fragment>
              ) : (
                part
              )
            )}
        </HeadingTag>
      </div>

      <dl class={style.data}>
        <div class={style.dataItem}>
          <dt class={style.label}>Capacity</dt>
          <dd class={style.value}>
            <span class={style.short}>{formatkWh(product.capacity)}</span>
            <span class={style.full}>{formatWh(product.capacity)}</span>
          </dd>
        </div>
        <div class={style.dataItem}>
          <dt class={style.label}>Port</dt>
          <dd class={style.value}>{product?.portType}</dd>
        </div>

        {system && system?.expansion?.portType != product?.portType ? (
          <div class={style.dataItem}>
            <dt class={style.label}>Adapter</dt>
            <dd class={style.value}>
              <span class={style.short}>Yes</span>
              <span class={style.full}>Required</span>
            </dd>
          </div>
        ) : null}
      </dl>

      <Buttons product={product} />

      {hasNotes ? (
        <div class={style.notes}>
          <ul>
            {notes?.map((note, index) => (
              <li key={index}>
                <RenderNote note={note} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const Buttons = ({ product }) => (
  <ul class={style.buttons}>
    {product.link && product.link.href ? (
      <li>
        <Button
          size="small"
          external
          affiliateData={product.link.affiliateData}
          affiliate={product.link.affiliate}
          href={product.link.href}
          icon={product.link.icon}
        >
          {product.brand}
        </Button>
      </li>
    ) : null}
    {product.amazon && product.amazon.href ? (
      <li>
        <Button
          size="small"
          external
          affiliate={product.amazon.affiliate}
          href={product.amazon.href}
          secondary
          icon="Amazon"
        >
          Amazon
        </Button>
      </li>
    ) : null}
    {product.review ? (
      <li>
        <Button
          size="small"
          secondary
          transparent
          href={product.review}
          icon="Matanich"
        >
          Review
        </Button>
      </li>
    ) : null}
  </ul>
);

const DataView = ({
  data,
  batteryData = null,
  powerStationData = null,
  headingLevel = 0,
  hideCompatibleHeader = false,
  includeUnofficialItems = false,
  CompatibilityIcon = null,
  openLegendDetailsModal = null,
}) => {
  const isBatteryFirstMode = Boolean(powerStationData);

  let ItemHeadingTag = "div";
  let CompatibleHeadingTag = "div";
  let SubItemHeadingTag = "div";
  if (headingLevel >= 1 && headingLevel <= 6) {
    ItemHeadingTag = `h${headingLevel}`;

    let increase = 0;
    if (!hideCompatibleHeader && headingLevel + 1 <= 6) {
      CompatibleHeadingTag = `h${headingLevel + 1}`;
      increase++;
    }

    if (headingLevel + (increase + 1) <= 6) {
      SubItemHeadingTag = `h${headingLevel + increase + 1}`;
    }
  }

  return (
    <ul class={style.cardList}>
      {data.systems.map((product, index) => {
        if (isBatteryFirstMode) {
          const batteryKey = product.name;

          // Gather all power stations compatible with this battery
          const allCompatibleStations = Object.values(
            powerStationData || {}
          ).flatMap((station) => {
            const batteryEntry = station.expansion?.compatibleBatteries?.find(
              (b) => b.name === product.name
            );
            return batteryEntry ? [{ station, batteryEntry }] : [];
          });

          // Separate into official and unofficial systems
          const officialStations = allCompatibleStations.filter(
            ({ batteryEntry }) => batteryEntry?.official === true
          );

          const sections = [
            { title: "Compatible Systems:", items: officialStations },
          ];

          if (includeUnofficialItems) {
            const unofficialStations = allCompatibleStations.filter(
              ({ batteryEntry }) => batteryEntry?.official !== true
            );
            sections.push({
              title:
                "May work, Not officially supported, Attempt at your own risk:",
              items: unofficialStations,
            });
          }

          return (
            <li key={batteryKey}>
              <Battery product={product} HeadingTag={ItemHeadingTag} />
              <div class={style.compatibleItems}>
                {sections.map(
                  ({ title, items }) =>
                    items.length > 0 && (
                      <div key={title} class={style.compatibleGroup}>
                        {!hideCompatibleHeader && (
                          <CompatibleHeadingTag class={style.heading}>
                            {title}
                          </CompatibleHeadingTag>
                        )}
                        <ul class={style.cardList}>
                          {items.map(({ station, batteryEntry }) => {
                            let compatibility = null;
                            if (batteryEntry.official === true) {
                              if (batteryEntry.type === "direct") {
                                compatibility = "direct";
                              } else if (batteryEntry.type === "adapter") {
                                compatibility = "adapter";
                              }
                            } else {
                              compatibility = "unofficial-bright";
                            }

                            return (
                              <li key={station.name}>
                                <PowerSystem
                                  product={station}
                                  battery={product}
                                  notes={[
                                    ...(station.expansion?.notes ?? []),
                                    ...(batteryEntry?.notes ?? []),
                                  ]}
                                  HeadingTag={SubItemHeadingTag}
                                />
                                {CompatibilityIcon && (
                                  <CompatibilityIcon
                                    class={style.compatibility}
                                    status={compatibility}
                                    onClick={openLegendDetailsModal}
                                  />
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )
                )}
              </div>
            </li>
          );
        }

        // Power Station first mode
        const compatibleBatteries =
          product.expansion?.compatibleBatteries || [];

        // Separate into official and unofficial batteries
        const officialBatteries = compatibleBatteries.filter(
          (battery) => battery?.official === true
        );

        const batterySections = [
          { title: "Compatible Batteries:", items: officialBatteries },
        ];

        if (includeUnofficialItems) {
          const unofficialBatteries = compatibleBatteries.filter(
            (battery) => battery?.official !== true
          );
          batterySections.push({
            title:
              "May work, Not officially supported, Attempt at your own risk:",
            items: unofficialBatteries,
          });
        }

        return (
          <li key={product.name}>
            <PowerSystem
              product={product}
              notes={product.expansion?.notes}
              HeadingTag={ItemHeadingTag}
            />
            {compatibleBatteries.length > 0 ? (
              <div class={style.compatibleItems}>
                {batterySections.map(
                  ({ title, items }) =>
                    items.length > 0 && (
                      <div key={title} class={style.compatibleGroup}>
                        {!hideCompatibleHeader && (
                          <CompatibleHeadingTag class={style.heading}>
                            {title}
                          </CompatibleHeadingTag>
                        )}
                        <ul class={style.cardList}>
                          {items.map((batteryEntry) => {
                            let compatibility = null;
                            if (batteryEntry.official === true) {
                              if (batteryEntry.type === "direct") {
                                compatibility = "direct";
                              } else if (batteryEntry.type === "adapter") {
                                compatibility = "adapter";
                              }
                            } else {
                              compatibility = "unofficial-bright";
                            }
                            return (
                              <li key={batteryEntry.name}>
                                <Battery
                                  product={batteryData?.[batteryEntry.name]}
                                  system={product}
                                  notes={batteryEntry?.notes}
                                  HeadingTag={SubItemHeadingTag}
                                />
                                {CompatibilityIcon && (
                                  <CompatibilityIcon
                                    class={style.compatibility}
                                    status={compatibility}
                                    onClick={openLegendDetailsModal}
                                  />
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )
                )}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export default DataView;
